/* eslint no-shadow: 0 no-param-reassign: 0 */

import Vue from 'vue';
import Vuex from 'vuex';
import { startOfDay, endOfDay, startOfMonth, endOfMonth, startOfWeek, endOfWeek, startOfYear, endOfYear } from 'date-fns';

import db from './db';

Vue.use(Vuex);

const debug = process.env.NODE_ENV !== 'production';

const state = {
  application: {
    installPrompt: null,
    loadingModal: '',
    searchQuery: '',
    stateInitialized: false,
    toasts: [],
    transactions: [],
    transactionPage: 0,
    transactionPageSize: null,
  },
  preferences: {
    currencySymbol: '€',
    suffixCurrencySymbol: true,
    defaultDescriptions: {
      book: 'Book',
      bread: 'Bread',
      burger: 'Fast Food',
      'chocolate-bar': 'Snack',
      cocktail: 'Bar',
      'coffee-to-go': 'Coffee',
      'credit-card': 'ATM Top-Up',
      diamond: 'Jewlry',
      gas: 'Gas',
      hairdryer: 'Beauty Parlour',
      'hand-lotion': 'Cosmetics',
      hanger: 'Clothing',
      'ice-cream': 'Ice Cream',
      lipstick: 'Make-Up',
      loan: 'Loan',
      misc: 'Miscellaneous',
      nailpolish: 'Nailpolish',
      payback: 'Payback',
      pharmacy: 'Medicine',
      pizza: 'Pizza',
      present: 'Gift',
      restaurant: 'Restaurant',
      scissors: 'Hairdresser',
      shirt: 'Clothing',
      'shopping-bag': 'Groceries',
      ticket: 'Ticket',
      travel: 'Trip',
    },
    positiveDefaults: [],
  },
  user: {
    currentWalletBalance: 0,
    forceChangelog: false,
    iconOrder: [
      'book',
      'bread',
      'burger',
      'chocolate-bar',
      'cocktail',
      'coffee-to-go',
      'credit-card',
      'diamond',
      'gas',
      'hairdryer',
      'hand-lotion',
      'hanger',
      'ice-cream',
      'lipstick',
      'loan',
      'misc',
      'nailpolish',
      'payback',
      'pharmacy',
      'pizza',
      'present',
      'restaurant',
      'scissors',
      'shirt',
      'shopping-bag',
      'ticket',
      'travel',
    ],
    onboardingComplete: false,
    prompted: false,
  },
};

const mutations = {
  addPositiveDefault(state, icon) {
    state.preferences.positiveDefaults.push(icon);
  },
  addToast(state, payload) {
    const toast = {
      ...payload,
    };
    if (!payload.id) toast.id = `_${Math.random().toString(36).substr(2, 9)}${state.application.toasts.length}`;
    state.application.toasts.push(toast);
    if (state.application.toasts.length > 4) state.application.toasts.shift();
  },
  addTransaction(state, payload) {
    state.application.transactions.unshift(payload);
  },
  addTransactionsArray(state, transactions) {
    state.application.transactions = state.application.transactions.concat(transactions);
  },
  addTransactionAtIndex(state, payload) {
    state.application.transactions.splice(payload.index, 0, payload.transaction);
  },
  clearToasts(state) {
    state.application.toasts = [];
  },
  deleteTransaction(state, id) {
    const { transactions } = state.application;
    for (let i = transactions.length - 1; i >= 0; i -= 1) {
      if (transactions[i].id === id) transactions.splice(i, 1);
    }
  },
  removePositiveDefault(state, icon) {
    const index = state.preferences.positiveDefaults.indexOf(icon);
    if (index > -1) state.preferences.positiveDefaults.splice(index, 1);
  },
  setForceChangelog(state, value) {
    state.user.forceChangelog = value;
  },
  setInstallPrompt(state, prompt) {
    state.application.installPrompt = prompt;
  },
  setLoadingModal(state, label) {
    state.application.loadingModal = label;
  },
  setOnboardingComplete(state) {
    state.user.onboardingComplete = true;
  },
  setPreferences(state, preferences) {
    state.preferences = preferences;
  },
  setPrompted(state, value) {
    state.user.prompted = value;
  },
  setStateInitialized(state, value) {
    state.application.stateInitialized = value;
  },
  setTransactions(state, transactions) {
    state.application.transactions = transactions;
  },
  setTransactionPage(state, page) {
    state.application.transactionPage = page;
  },
  setTransactionPageSize(state, size) {
    state.application.transactionPageSize = size;
  },
  setUser(state, user) {
    state.user = user;
  },
  updateCurrencySymbol(state, payload) {
    state.preferences.currencySymbol = payload.value;
  },
  updateCurrentWalletBalance(state, value) {
    state.user.currentWalletBalance = value;
  },
  updateDefaultDescriptions(state, payload) {
    state.preferences.defaultDescriptions[payload.field] = payload.value;
  },
  updateIconOrder(state, icon) {
    const currentIconIndex = state.user.iconOrder.indexOf(icon);

    if (currentIconIndex > 0) {
      state.user.iconOrder.splice(currentIconIndex, 1);
      state.user.iconOrder.unshift(icon);
    }
  },
  updateQuery(state, payload) {
    state.application.searchQuery = payload.query;
  },
  updateSuffixCurrencySymbol(state, payload) {
    state.preferences.suffixCurrencySymbol = payload.value;
  },
  removeToast(state, payload) {
    for (let i = state.application.toasts.length - 1; i >= 0; i -= 1) {
      const toast = state.application.toasts[i];
      if (toast.id === payload.toastId) state.application.toasts.splice(i, 1);
    }
  },
};

const actions = {
  addPositiveDefault({ commit, dispatch }, icon) {
    commit('addPositiveDefault', icon);
    return dispatch('savePreferences');
  },
  addTransaction({ commit, dispatch, state }, payload) {
    return new Promise((resolve, reject) => {
      const transaction = { ...payload };
      if (!payload.createdAt) transaction.createdAt = Date.now();
      db.transactions.add(transaction)
        .then((id) => {
          transaction.id = id;
          commit('addTransaction', transaction);
        })
        .then(() => dispatch('updateCurrentWalletBalance', state.user.currentWalletBalance + payload.amount))
        .then(() => resolve())
        .catch(err => reject(err));
    });
  },
  cancelSearch({ commit, dispatch, state }) {
    return new Promise((resolve, reject) => {
      dispatch('initializeMatchingTransactions')
        .then(() => {
          if (state.application.transactionPage > 0) commit('setTransactionPage', 0);
          commit('updateQuery', { query: null });
          resolve();
        })
        .catch(err => reject(err));
    });
  },
  deleteTransaction({ commit, dispatch, state }, id) {
    return new Promise((resolve, reject) => {
      const transactionBackup = state.application.transactions.find(element => element.id === id);
      const transactionIndex =
        state.application.transactions.findIndex(element => element.id === id);
      const timeout = 5000;

      commit('deleteTransaction', id);
      commit('updateCurrentWalletBalance', state.user.currentWalletBalance - transactionBackup.amount);

      const timeoutId = window.setTimeout(() => {
        db.transactions.delete(id)
          .then(() => dispatch('saveUser'))
          .then(() => resolve(true)).catch(err => reject(err));
      }, timeout + 100);

      commit('addToast', {
        content: 'Transaction deleted',
        color: 'negative',
        action: 'Undo',
        timeout,
        callback: () => {
          window.clearTimeout(timeoutId);
          commit('addTransactionAtIndex', { transaction: transactionBackup, index: transactionIndex });
          dispatch('updateCurrentWalletBalance', state.user.currentWalletBalance + transactionBackup.amount).then(() => resolve(false));
        },
      });
    });
  },
  exportData() {
    return new Promise((resolve, reject) => {
      db.transaction('r', db.appData, db.transactions, () => {
        const preferences = db.appData.get('preferences');
        const user = db.appData.get('user');
        const transactions = db.transactions.toArray();

        return Promise.all([preferences, user, transactions]);
      })
        .then((data) => {
          resolve({
            appData: {
              preferences: data[0],
              user: data[1],
            },
            transactions: data[2],
          });
        })
        .catch(err => reject(err));
    });
  },
  importData({ state }, data) {
    return new Promise((resolve, reject) => {
      // validate the data
      if (!data.appData || !data.appData.user || !data.transactions) {
        reject(new Error('Invalid Data'));
        return;
      }

      // make sure we insert any missing properties if necessary
      const { iconOrder } = data.appData.user;

      let defaultDescriptions;
      if (data.appData.preferences) ({ defaultDescriptions } = data.appData.preferences);

      if (iconOrder && iconOrder.length < state.user.iconOrder.length) {
        state.user.iconOrder.forEach((entry) => {
          if (iconOrder.indexOf(entry) < 0) iconOrder.push(entry);
        });
      }

      if (defaultDescriptions && Object.keys(defaultDescriptions).length < Object.keys(state.preferences.defaultDescriptions).length) {
        const mergedDescriptions = { ...state.preferences.defaultDescriptions, ...defaultDescriptions };
        data.appData.preferences.defaultDescriptions = mergedDescriptions;
      }

      // if it’s valid, import it
      db.transaction('rw', db.appData, db.transactions, () => {
        let preferences;
        if (data.appData.preferences) preferences = db.appData.put(data.appData.preferences, 'preferences');
        const user = db.appData.put(data.appData.user, 'user');
        const transactions = db.transactions.clear()
          .then(() => db.transactions.bulkAdd(data.transactions));

        return Promise.all([preferences, user, transactions]);
      })
        .then(() => resolve())
        .catch(err => reject(err));
    });
  },
  initializeMatchingTransactions({ commit, state }) {
    return new Promise((resolve) => {
      db.transactions
        .orderBy('createdAt')
        .limit(state.application.transactionPageSize)
        .reverse()
        .toArray()
        .then((transactions) => {
          commit('setTransactions', transactions);
          resolve();
        });
    });
  },
  initializeRecentTransactions({ commit }) {
    // return new Promise((resolve, reject) => {
    //   db.transactions.orderBy('createdAt')
    //     .limit(7)
    //     .reverse()
    //     .toArray()
    //     .then((transactions) => {
    //       commit('setTransactions', transactions);
    //       resolve();
    //     })
    //     .catch(err => reject(err));
    // });
    // needs to load the past month because of the spending calculation
    return new Promise((resolve, reject) => {
      const startingPoint = Math.min(startOfWeek(Date.now(), { weekStartsOn: 1 }), startOfMonth(Date.now()));
      db.transactions.where('createdAt')
        .aboveOrEqual(startingPoint)
        .reverse()
        .toArray()
        .then((transactions) => {
          if (transactions.length < 7) {
            const paddingAmount = 7 - transactions.length;
            db.transactions
              .where('createdAt')
              .below(startingPoint)
              .limit(paddingAmount)
              .reverse()
              .toArray()
              .then((paddingTransactions) => {
                commit('setTransactions', transactions.concat(paddingTransactions));
                resolve();
              })
              .catch(err => reject(err));
          } else {
            commit('setTransactions', transactions);
            resolve();
          }
        })
        .catch(err => reject(err));
    });
  },
  initializeState({ commit, state }) {
    return new Promise((resolve, reject) => {
      db.transaction('r', db.appData, () => {
        const user = db.appData.get('user');
        const preferences = db.appData.get('preferences');

        return Promise.all([user, preferences]);
      }).then((data) => {
        const [user, preferences] = data;
        if (user) {
          const { iconOrder } = user;

          // check if all icons are in the order
          if (iconOrder.length < state.user.iconOrder.length) {
            // find and add the missing ones
            state.user.iconOrder.forEach((entry) => {
              if (iconOrder.indexOf(entry) < 0) iconOrder.push(entry);
            });
          }
          commit('setUser', user);
        }
        if (preferences) {
          const { defaultDescriptions } = preferences;

          // check if there’s a description for each icon
          if (Object.keys(defaultDescriptions).length < Object.keys(state.preferences.defaultDescriptions).length) {
            // find and add the missing ones
            const mergedDescriptions = { ...state.preferences.defaultDescriptions, ...defaultDescriptions };
            preferences.defaultDescriptions = mergedDescriptions;
          }
          if (!preferences.positiveDefaults) preferences.positiveDefaults = [];
          commit('setPreferences', preferences);
        }
        commit('setStateInitialized', true);
        resolve();
      }).catch(err => reject(err));
    });
  },
  loadNextMatchingTransactionsPage({ commit, state }) {
    return new Promise((resolve) => {
      commit('setTransactionPage', state.application.transactionPage + 1);
      if (state.application.transactions.length < state.application.transactionPageSize * state.application.transactionPage) {
        commit('setTransactionPage', state.application.transactionPage - 1);
        resolve();
        return;
      }
      const lastEntry = state.application.transactions[state.application.transactions.length - 1];

      db.transactions
        .where('createdAt')
        .below(lastEntry.createdAt)
        .limit(state.application.transactionPageSize)
        .reverse()
        .toArray()
        .then((transactions) => {
          commit('addTransactionsArray', transactions);
          resolve();
        });
    });
  },
  removePositiveDefault({ commit, dispatch }, icon) {
    commit('removePositiveDefault', icon);
    return dispatch('savePreferences');
  },
  savePreferences({ state }) {
    return db.appData.put(state.preferences, 'preferences');
  },
  saveUser({ state }) {
    return db.appData.put(state.user, 'user');
  },
  search({ commit }, { query, range, type, filter }) { // eslint-disable-line object-curly-newline
    return new Promise((resolve, reject) => {
      if (type === 'date') {
        if (range === 'day') {
          db.transactions.where('createdAt')
            .between(startOfDay(query).valueOf(), endOfDay(query).valueOf(), true, true)
            .filter((transaction) => {
              if (filter === null) return true;
              return transaction.description.toLowerCase().indexOf(filter) > -1;
            })
            .reverse()
            .toArray()
            .then((transactions) => {
              commit('setTransactions', transactions);
              resolve();
            })
            .catch(err => reject(err));
        } else if (range === 'week') {
          db.transactions.where('createdAt')
            .between(
              startOfWeek(query, { weekStartsOn: 1 }).valueOf(),
              endOfWeek(query, { weekStartsOn: 1 }).valueOf(),
              true, true,
            )
            .filter((transaction) => {
              if (filter === null) return true;
              return transaction.description.toLowerCase().indexOf(filter) > -1;
            })
            .reverse()
            .toArray()
            .then((transactions) => {
              commit('setTransactions', transactions);
              resolve();
            })
            .catch(err => reject(err));
        } else if (range === 'month') {
          db.transactions.where('createdAt')
            .between(startOfMonth(query).valueOf(), endOfMonth(query).valueOf(), true, true)
            .filter((transaction) => {
              if (filter === null) return true;
              return transaction.description.toLowerCase().indexOf(filter) > -1;
            })
            .reverse()
            .toArray()
            .then((transactions) => {
              commit('setTransactions', transactions);
              resolve();
            })
            .catch(err => reject(err));
        } else if (range === 'year') {
          db.transactions.where('createdAt')
            .between(startOfYear(query).valueOf(), endOfYear(query).valueOf(), true, true)
            .filter((transaction) => {
              if (filter === null) return true;
              return transaction.description.toLowerCase().indexOf(filter) > -1;
            })
            .reverse()
            .toArray()
            .then((transactions) => {
              commit('setTransactions', transactions);
              resolve();
            })
            .catch(err => reject(err));
        }
      } else {
        // db.transactions.where('description')
        //   .startsWithIgnoreCase(query)
        //   .reverse()
        //   .sortBy('createdAt')
        //   .then((transactions) => {
        //     commit('setTransactions', transactions);
        //     console.log(`Duration: ${Date.now() - start}ms`);
        //     resolve();
        //   })
        //   .catch(err => reject(err));

        // perform a fuzzy search on the description, not utilising an index, might have performance ramifications
        // testing with 10 000 entries revealed that queries with fewer results are slower, but queries with many results marginally faster
        db.transactions
          .filter(transaction => transaction.description.toLowerCase().indexOf(query) > -1)
          .reverse()
          .sortBy('createdAt')
          .then((transactions) => {
            commit('setTransactions', transactions);
            resolve();
          })
          .catch(err => reject(err));
      }
    });
  },
  setForceChangelog({ commit, dispatch }, value) {
    commit('setForceChangelog', value);
    return dispatch('saveUser');
  },
  setOnboardingComplete({ commit, dispatch }) {
    commit('setOnboardingComplete');
    return dispatch('saveUser');
  },
  setUserPrompted({ commit, dispatch }, value) {
    commit('setPrompted', value);
    return dispatch('saveUser');
  },
  updateCurrencySymbol({ commit, dispatch }, payload) {
    commit('updateCurrencySymbol', payload);
    return dispatch('savePreferences');
  },
  updateCurrentWalletBalance({ commit, dispatch }, value) {
    commit('updateCurrentWalletBalance', value);
    return dispatch('saveUser');
  },
  updateDefaultDescriptions({ commit, dispatch }, payload) {
    commit('updateDefaultDescriptions', payload);
    return dispatch('savePreferences');
  },
  updateIconOrder({ commit, dispatch }, icon) {
    commit('updateIconOrder', icon);
    return dispatch('saveUser');
  },
  updateSuffixCurrencySymbol({ commit, dispatch }, payload) {
    commit('updateSuffixCurrencySymbol', payload);
    return dispatch('savePreferences');
  },
};

export default new Vuex.Store({
  state,
  mutations,
  actions,
  strict: debug,
});
